// src/views/AccommodationProvider/ConfigureBlocks.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ConfigureBlocks = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  
  // Get provider information from localStorage
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  const [property, setProperty] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (property) {
      return `Configure Blocks - ${property.trading_name || `Property #${property.id}`}`;
    }
    return 'Configure Blocks';
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  // Fetch property and blocks data
  useEffect(() => {
    const fetchData = async () => {
      if (!propertyId || regStatus !== '2') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch property details
        const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`);
        if (propertyResponse.status === 200) {
          setProperty(propertyResponse.data);
          
          // Check if property has any room types configured
          try {
            const roomTypesResponse = await axiosInstance.get(`/api/room-types/?property=${propertyId}`);
            if (roomTypesResponse.status === 200 && roomTypesResponse.data.length === 0) {
              // No room types exist - show warning
              setAlert({
                type: 'warning',
                message: 'Before you can configure blocks, you need to configure at least one room type which will be applied to the rooms you create. Please create room types first.',
                visible: true
              });
              setLoading(false);
              return;
            }
          } catch (roomTypeError) {
            console.error('Error checking room types:', roomTypeError);
          }
          
          // Fetch blocks from API
          try {
            const blocksResponse = await axiosInstance.get(`/api/properties/${propertyId}/blocks/`);
            if (blocksResponse.status === 200) {
              setBlocks(blocksResponse.data);
            }
          } catch (blockError) {
            console.error('Error fetching blocks:', blockError);
            // If blocks endpoint fails, create placeholder blocks based on no_of_blocks
            const noOfBlocks = propertyResponse.data.no_of_blocks || 0;
            const placeholderBlocks = [];
            
            for (let i = 1; i <= noOfBlocks; i++) {
              placeholderBlocks.push({
                id: i,
                block_number: String(i).padStart(2, '0'),
                block_name: 'Not Configured',
                occupation_date: null,
                gender: null,
                no_of_floors: null
              });
            }
            
            setBlocks(placeholderBlocks);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load blocks. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propertyId, regStatus]);

  const handleBackToProperties = () => {
    navigate('/provider-properties');
  };

  const handleViewBlock = (blockId) => {
    // TODO: Implement view block functionality
    console.log(`View block ${blockId}`);
    // navigate(`/provider-dashboard/view-block/${propertyId}/${blockId}`);
  };

  const handleMaintainBlock = (blockId) => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}/maintain/${blockId}`);
  };

  const handleConfigureFloors = (blockId) => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}/block/${blockId}/configure-floors`);
  };

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleBackToProperties}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Properties
            </Button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col>
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({ ...alert, visible: false })}
            >
              {alert.message}
              {alert.type === 'warning' && blocks.length === 0 && (
                <div className="mt-3">
                  <Button 
                    color="primary" 
                    onClick={() => navigate(`/provider-dashboard/create-room-type/${propertyId}`)}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Create Room Types
                  </Button>
                </div>
              )}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Blocks Table */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-building me-2 text-primary"></i>
                Property Blocks
              </CardTitle>
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Loading blocks...</p>
                </div>
              ) : (
                <>
                  {blocks.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-building-slash display-4 text-muted"></i>
                      <h5 className="mt-3 text-muted">No Blocks Found</h5>
                      <p className="text-muted">
                        This property does not have any blocks configured yet.
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ paddingBottom: '150px' }}>
                      <Table hover>
                        <thead>
                          <tr>
                            <th>Block</th>
                            <th>Block Name</th>
                            <th>Occupation Date</th>
                            <th>Gender</th>
                            <th>No Of Floors</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {blocks.map((block) => (
                            <tr key={block.id}>
                              <td>
                                <div className="fw-bold">
                                  {block.block_number}
                                </div>
                              </td>
                              <td>
                                <span className="text-muted">{block.block_name || 'Not Configured'}</span>
                              </td>
                              <td>
                                <span className="text-muted">{block.occupation_date || 'Not Set'}</span>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {block.gender ? block.gender.charAt(0).toUpperCase() + block.gender.slice(1) : 'Not Set'}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted">{block.no_of_floors || 'Not Set'}</span>
                              </td>
                              <td className="text-center">
                                <UncontrolledDropdown>
                                  <DropdownToggle 
                                    color="light" 
                                    size="sm" 
                                    className="btn-outline-secondary"
                                    caret={false}
                                  >
                                    <i className="bi bi-three-dots"></i>
                                  </DropdownToggle>
                                  <DropdownMenu end>
                                    <DropdownItem 
                                      onClick={() => handleViewBlock(block.id)}
                                    >
                                      <i className="bi bi-eye me-2"></i>
                                      View
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleMaintainBlock(block.id)}
                                    >
                                      <i className="bi bi-gear me-2"></i>
                                      Configure Block
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleConfigureFloors(block.id)}
                                    >
                                      <i className="bi bi-layers me-2"></i>
                                      Configure Floors
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Action Summary */}
      {blocks.length > 0 && (
        <Row className="mt-4">
          <Col md="12">
            <Card className="border-0 bg-light">
              <CardBody>
                <CardTitle tag="h6" className="mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Block Management Actions
                </CardTitle>
                <Row>
                  <Col md="6">
                    <div className="mb-2">
                      <strong>View:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>View block details and information</li>
                      <li>See current block configuration</li>
                      <li>Review block occupancy status</li>
                    </ul>
                  </Col>
                  <Col md="6">
                    <div className="mb-2">
                      <strong>Configure Block:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>Edit block information</li>
                      <li>Update block settings</li>
                      <li>Manage block configuration</li>
                    </ul>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ConfigureBlocks;
